import { Button, ButtonGroup, cn } from "@heroui/react";
import {
    IconBold,
    IconLink,
    IconLinkOff,
    IconList,
    IconUser,
} from "@tabler/icons-react";
import HardBreak from "@tiptap/extension-hard-break";
import Link from "@tiptap/extension-link";
import { TextStyleKit } from "@tiptap/extension-text-style";
import type { Editor } from "@tiptap/react";
import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect, useMemo } from "react";
import { Tooltip } from "../tooltip";
import { LeaveListOnBackspace } from "./leave-list-on-backspace";
import { pasteHandler } from "./paste-handler";

/**
 * Enter puts in a line break instead of starting a paragraph. The email
 * editors are written for that: their content is one block of text, and real
 * paragraphs would space it out.
 *
 * It costs the editor everything which begins at the start of a paragraph
 * though, a bullet point typed as a dash above all, since after such a break
 * the cursor is still in the middle of the same one. Editors which need that
 * ask for paragraphs instead.
 */
const HardBreakOnEnter = HardBreak.extend({
    addKeyboardShortcuts() {
        return {
            /**
             * Shift-Enter and Mod-Enter come with the extension and are spread
             * back in, since naming a shortcut here replaces the whole set.
             * Inside a list they are the only way left to break a line without
             * starting a new bullet point.
             */
            ...this.parent?.(),
            /**
             * Inside a list Enter has to end one bullet point and start the
             * next, so there it is handed on untouched.
             */
            Enter: () =>
                this.editor.isActive("listItem")
                    ? false
                    : this.editor.commands.setHardBreak(),
        };
    },
});

const generateExtensions = (withParagraphOnEnter: boolean) => [
    TextStyleKit,
    StarterKit.configure({
        hardBreak: false,
        link: false,
    }),
    withParagraphOnEnter ? HardBreak : HardBreakOnEnter,
    LeaveListOnBackspace,
    Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: "https",
        protocols: ["http", "https"],
        HTMLAttributes: {
            class: "underline pointer-events-none",
        },
    }),
];

const RichText__MenuBar = ({
    editor,
    withBulletList,
    withUserName,
}: {
    editor: Editor;
    withBulletList?: boolean;
    withUserName?: boolean;
}) => {
    // Read the current editor's state, and re-render the component when it changes
    const editorState = useEditorState({
        editor,
        selector: (ctx) => {
            return {
                isBold: ctx.editor.isActive("bold") ?? false,
                canBold: ctx.editor.can().chain().toggleBold().run() ?? false,
                isItalic: ctx.editor.isActive("italic") ?? false,
                canItalic:
                    ctx.editor.can().chain().toggleItalic().run() ?? false,
                isStrike: ctx.editor.isActive("strike") ?? false,
                canStrike:
                    ctx.editor.can().chain().toggleStrike().run() ?? false,
                isCode: ctx.editor.isActive("code") ?? false,
                canCode: ctx.editor.can().chain().toggleCode().run() ?? false,
                canClearMarks:
                    ctx.editor.can().chain().unsetAllMarks().run() ?? false,
                isParagraph: ctx.editor.isActive("paragraph") ?? false,
                isHeading1:
                    ctx.editor.isActive("heading", { level: 1 }) ?? false,
                isHeading2:
                    ctx.editor.isActive("heading", { level: 2 }) ?? false,
                isHeading3:
                    ctx.editor.isActive("heading", { level: 3 }) ?? false,
                isHeading4:
                    ctx.editor.isActive("heading", { level: 4 }) ?? false,
                isHeading5:
                    ctx.editor.isActive("heading", { level: 5 }) ?? false,
                isHeading6:
                    ctx.editor.isActive("heading", { level: 6 }) ?? false,
                isBulletList: ctx.editor.isActive("bulletList") ?? false,
                isOrderedList: ctx.editor.isActive("orderedList") ?? false,
                isCodeBlock: ctx.editor.isActive("codeBlock") ?? false,
                isBlockquote: ctx.editor.isActive("blockquote") ?? false,
                canUndo: ctx.editor.can().chain().undo().run() ?? false,
                canRedo: ctx.editor.can().chain().redo().run() ?? false,
                isLink: ctx.editor.isActive("link"),
            };
        },
    });

    return (
        <ButtonGroup>
            <Button
                variant={editorState.isBold ? "solid" : "flat"}
                size="sm"
                onPress={() => editor.chain().focus().toggleBold().run()}
                isDisabled={!editorState.canBold}
                isIconOnly
                tabIndex={-1}
            >
                <IconBold size={18} stroke={1.5} />
            </Button>
            {withBulletList && (
                <Button
                    variant={editorState.isBulletList ? "solid" : "flat"}
                    size="sm"
                    onPress={() =>
                        editor.chain().focus().toggleBulletList().run()
                    }
                    isIconOnly
                    tabIndex={-1}
                >
                    <IconList size={18} stroke={1.5} />
                </Button>
            )}
            <Button
                variant={editorState.isLink ? "solid" : "flat"}
                size="sm"
                onPress={() => {
                    const url = window.prompt("Link");

                    if (url) {
                        editor.chain().focus().setLink({ href: url }).run();
                    }
                }}
                isIconOnly
                tabIndex={-1}
            >
                <IconLink size={18} stroke={1.5} />
            </Button>
            <Button
                variant="flat"
                color={editorState.isLink ? "danger" : "default"}
                size="sm"
                isDisabled={!editorState.isLink}
                onPress={() => editor.chain().focus().unsetLink().run()}
                isIconOnly
                tabIndex={-1}
            >
                <IconLinkOff size={18} stroke={1.5} />
            </Button>
            {withUserName && (
                <Button
                    variant="flat"
                    size="sm"
                    onPress={() =>
                        editor
                            .chain()
                            .focus()
                            .insertContent("{{bewerberName}}")
                            .run()
                    }
                    isIconOnly
                    tabIndex={-1}
                >
                    <Tooltip
                        content="Platzhalter für Kandidatennamen"
                        delay={500}
                    >
                        <IconUser size={18} stroke={1.5} />
                    </Tooltip>
                </Button>
            )}
        </ButtonGroup>
    );
};

const RichText = ({
    content,
    className,
    isDisabled,
    isViewOnly,
    withBulletList,
    withParagraphOnEnter,
    withUserName,
    onChange,
}: {
    content: string;
    className?: string;
    isDisabled?: boolean;
    isViewOnly?: boolean;
    withBulletList?: boolean;
    withParagraphOnEnter?: boolean;
    withUserName?: boolean;
    onChange?: (value: string) => void;
}) => {
    /**
     * Held on to, since handing the editor a new set on every render would
     * throw away what is being typed.
     */
    const extensions = useMemo(
        () => generateExtensions(!!withParagraphOnEnter),
        [withParagraphOnEnter],
    );

    const editor = useEditor({
        extensions,
        content,
        autofocus: false,
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            onChange?.(html);
        },
        editorProps: {
            handlePaste: pasteHandler,
        },
        editable: false,
    });

    /**
     * This is a workaround to prevent the editor from being focussed when mounted
     */
    useEffect(() => {
        const timer = window.setTimeout(() => {
            editor.setEditable(!isDisabled && !isViewOnly, false);
        }, 200);

        return () => {
            window.clearTimeout(timer);
        };
    }, [isDisabled, isViewOnly, editor]);

    if (!editor) {
        return null;
    }

    return (
        <div
            className={cn(
                "tap-highlight-transparent flex flex-col rounded-medium bg-default-100",
                isViewOnly && "pointer-events-none opacity-70",
                className,
            )}
        >
            <div className={cn("px-3 pt-3", isViewOnly && "pt-1")}>
                <div className={cn(isViewOnly && "hidden")}>
                    <RichText__MenuBar
                        editor={editor}
                        withBulletList={withBulletList}
                        withUserName={withUserName}
                    />
                </div>
            </div>
            <div className="px-3">
                <EditorContent
                    editor={editor}
                    className="prose text-foreground text-small"
                />
            </div>
        </div>
    );
};

export { RichText };
